import React from 'react';
import { useTranslation } from 'react-i18next';

export interface IAreaCapacitySlice {
    id: string;
    name: string;
    capacity: number;
}

interface IProps {
    slices: IAreaCapacitySlice[];
    activeId: string;
    label: string;
}

const SIZE = 32;
const RADIUS = SIZE / 2;

const pointAt = (fraction: number) => {
    // Start at 12 o'clock and run clockwise.
    const angle = fraction * 2 * Math.PI - Math.PI / 2;
    return `${RADIUS + RADIUS * Math.cos(angle)} ${RADIUS + RADIUS * Math.sin(angle)}`;
};

const slicePath = (from: number, to: number) => {
    const largeArc = to - from > 0.5 ? 1 : 0;
    return `M ${RADIUS} ${RADIUS} L ${pointAt(from)} A ${RADIUS} ${RADIUS} 0 ${largeArc} 1 ${pointAt(to)} Z`;
};

// Every area is a slice, in list order; the row's own area is highlighted and the rest stay grey.
export const AreaCapacityPie: React.FC<IProps> = ({ slices, activeId, label }) => {
    const { t } = useTranslation();

    const total = slices.reduce((sum, slice) => sum + Math.max(slice.capacity, 0), 0);
    if (total <= 0) {
        return null;
    }

    let start = 0;
    return (
        <svg className="area-capacity-pie" viewBox={`0 0 ${SIZE} ${SIZE}`} role="img" aria-label={label}>
            {slices.map((slice) => {
                const fraction = Math.max(slice.capacity, 0) / total;
                if (fraction <= 0) {
                    return null;
                }
                const from = start;
                start += fraction;
                const className = `area-capacity-pie__slice${slice.id === activeId ? ' is-active' : ''}`;
                const title = `${slice.name} · ${t('sectors.seats', { count: slice.capacity })} · ${Math.round(
                    fraction * 100,
                )}%`;
                return fraction >= 1 ? (
                    <circle key={slice.id} className={className} cx={RADIUS} cy={RADIUS} r={RADIUS}>
                        <title>{title}</title>
                    </circle>
                ) : (
                    <path key={slice.id} className={className} d={slicePath(from, start)}>
                        <title>{title}</title>
                    </path>
                );
            })}
        </svg>
    );
};
